// Automatic saves in History (Josh, 5-6 Oct 2026). Two kinds of version are made
// without the user typing a name, and each gets its own folder in the panel:
//   'page_op'        "Save & Continue" before deleting, inserting or replacing pages
//   'promote_backup' the live project, saved just before an older version is made live
// On projects stamped `autoSaveCap: true` at creation, each folder keeps its newest
// AUTO_SAVE_CAP; older projects keep everything. Kept free of Vue/Firebase so the
// rules are unit-tested (history-autosave.test.js).

export const AUTO_SAVE_CAP = 10;
export const AUTO_SAVE_KINDS = ['page_op', 'promote_backup'];

const PAGE_OP_NAME = /^PDF Adjustment – /;
const PROMOTE_NAME = /^Before making ".*" live$/;

// A version made before the flag existed has no `autoSave` field at all and is
// recognised by its generated name. Once the field exists it alone decides: a
// rename writes `autoSave: null`, which keeps the version for good even if the
// user happens to keep a generated-looking name.
export function classifyVersion(version) {
    if (!version) return null;
    if (Object.prototype.hasOwnProperty.call(version, 'autoSave')) {
        return AUTO_SAVE_KINDS.indexOf(version.autoSave) > -1 ? version.autoSave : null;
    }
    const name = typeof version.name === 'string' ? version.name : '';
    if (PAGE_OP_NAME.test(name)) return 'page_op';
    if (PROMOTE_NAME.test(name)) return 'promote_backup';
    return null;
}

function createdMs(version) {
    const t = new Date(version.created).getTime();
    return isNaN(t) ? 0 : t;
}

// Ids to delete so `kind` keeps its newest `cap`. Never touches a named version,
// never runs on an exempt project, and never deletes a protected id (the version
// open on screen); a protected one still counts toward the cap.
export function selectPrune(versions, kind, options) {
    const opts = options || {};
    if (opts.capEnabled !== true) return [];
    const cap = typeof opts.cap === 'number' ? opts.cap : AUTO_SAVE_CAP;
    const protect = opts.protectIds || [];
    return (versions || [])
        .filter(v => v && v.id && classifyVersion(v) === kind)
        .sort((a, b) => createdMs(b) - createdMs(a))
        .slice(cap)
        .filter(v => protect.indexOf(v.id) === -1)
        .reverse()
        .map(v => v.id);
}

function pages(from, to) {
    return String(from) === String(to) ? `page ${from}` : `pages ${from} to ${to}`;
}

// The second line of a page-change row, written when the save is made.
// from/to/at are page LABELS (pdfPageNumbers[].name), e.g. "12A".
export function pageOpDetail(info) {
    if (!info) return '';
    if (info.op === 'delete' || info.op === 'replace') {
        if (info.from === undefined || info.from === null || info.from === '') return '';
        const to = info.to === undefined || info.to === null || info.to === '' ? info.from : info.to;
        return `before ${info.op === 'delete' ? 'deleting' : 'replacing'} ${pages(info.from, to)}`;
    }
    if (info.op === 'insert') {
        if (info.at === undefined || info.at === null || info.at === '') return '';
        const where = info.where === 'before' ? 'before' : 'after';
        const n = Number(info.count);
        const what = n > 0 ? `${n} page${n === 1 ? '' : 's'}` : 'pages';
        return `before inserting ${what} ${where} ${info.at}`;
    }
    return '';
}

export function promoteDetail(versionName) {
    return `before making "${versionName}" live`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortWhen(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${h % 12 || 12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
}

export function rowTitle(version, kind) {
    return `${kind === 'promote_backup' ? 'Backup' : 'Page change'} · ${shortWhen(version.created)}`;
}

export function rowDetail(version, kind) {
    if (version.autoSaveDetail) return version.autoSaveDetail;
    if (kind === 'promote_backup' && typeof version.name === 'string') {
        return version.name.charAt(0).toLowerCase() + version.name.slice(1);
    }
    return 'saved before a page change';
}


// WEBPACK FOOTER //
// ./src/lib/history-autosave.js